import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControl,
  FormControlLabel,
  Alert,
  Snackbar,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { Grid } from '../components/common/TypedGrid';
import {
  Edit as EditIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  DevicesOther as DevicesIcon,
} from '@mui/icons-material';
import { useAdmin } from '../contexts/AdminContext';
import { useNotifications } from '../contexts/NotificationContext';
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import adminService from '../services/adminService';
import { PageHeader } from '../components/common';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
      style={{ minHeight: '300px', overflow: 'auto' }}
    >
      {value === index && <Box sx={{ pt: 3, pb: 3, px: 1 }}>{children}</Box>}
    </div>
  );
}

const AdminProfile: React.FC = () => {
  const { user } = useAdmin();
  const { fcmPermission, requestNotificationPermission } = useNotifications();
  const [currentTab, setCurrentTab] = useState(0);
  
  // Profile Info State
  const [profileInfo, setProfileInfo] = useState({
    displayName: user?.displayName || 'Admin User',
    email: user?.email || '',
    phone: user?.phoneNumber || '',
    profilePicture: user?.photoURL || '',
  });
  const [editingProfile, setEditingProfile] = useState(false);

  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);

  // Preferences State
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    pushNotifications: true,
    taskNotifications: true,
    securityAlerts: true,
    darkMode: false,
    language: 'en',
    timezone: 'Asia/Kolkata',
  });

  // Security State
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    sessionTimeout: 30,
    requirePasswordChange: false,
  });

  // Activity Log State
  interface ActivityLogItem {
    id: string;
    action: string;
    timestamp: Date;
    ip: string;
    userAgent?: string;
  }
  const [activityLog, setActivityLog] = useState<ActivityLogItem[]>([]);

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load preferences
        const userPreferences = await adminService.getAdminPreferences();
        if (userPreferences) {
          setPreferences(userPreferences);
        }

        // Load security settings
        const userSecuritySettings = await adminService.getSecuritySettings();
        if (userSecuritySettings) {
          setSecuritySettings(userSecuritySettings);
        }

        // Load activity log
        const activities = await adminService.getAdminActivityLog();
        if (activities.length > 0) {
          // Update activity log state if needed
          setActivityLog(activities);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        showSnackbar('Error loading profile data', 'error');
      }
    };

    loadInitialData();
  }, []);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Profile Info Handlers
  const handleProfileSave = async () => {
    setLoading(true);
    try {
      const success = await adminService.updateAdminProfile(profileInfo);
      if (success) {
        showSnackbar('Profile updated successfully', 'success');
        setEditingProfile(false);
        // Log the activity
        await adminService.logAdminActivity('Updated profile information');
      } else {
        showSnackbar('Error updating profile', 'error');
      }
    } catch (error) {
      showSnackbar('Error updating profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Password Change Handlers
  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showSnackbar('New passwords do not match', 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showSnackbar('Password must be at least 6 characters long', 'error');
      return;
    }

    if (!passwordData.currentPassword) {
      showSnackbar('Please enter your current password', 'error');
      return;
    }

    setLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user || !user.email) {
        throw new Error('No authenticated user found');
      }
      
      // Re-authenticate the user with their current password
      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update the password
      await updatePassword(user, passwordData.newPassword);
      
      showSnackbar('Password changed successfully', 'success');
      setPasswordChangeOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      // Log the activity
      await adminService.logAdminActivity('Changed password');
    } catch (error: any) {
      console.error('Error changing password:', error);
      let errorMessage = 'Error changing password. Please check your current password.';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log out and log back in before changing your password.';
      }
      
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Preferences Handlers
  const handlePreferenceSave = async () => {
    setLoading(true);
    try {
      const success = await adminService.updateAdminPreferences(preferences);
      if (success) {
        showSnackbar('Preferences saved successfully', 'success');
        // Log the activity
        await adminService.logAdminActivity('Updated preferences');
      } else {
        showSnackbar('Error saving preferences', 'error');
      }
    } catch (error) {
      showSnackbar('Error saving preferences', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Security Settings Handlers
  const handleSecuritySave = async () => {
    setLoading(true);
    try {
      const success = await adminService.updateSecuritySettings(securitySettings);
      if (success) {
        showSnackbar('Security settings updated successfully', 'success');
        // Log the activity
        await adminService.logAdminActivity('Updated security settings');
      } else {
        showSnackbar('Error updating security settings', 'error');
      }
    } catch (error) {
      showSnackbar('Error updating security settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAllDevices = async () => {
    if (window.confirm('Are you sure you want to logout from all devices? You will need to login again.')) {
      try {
        const success = await adminService.logoutAllDevices();
        if (success) {
          showSnackbar('Logged out from all devices successfully', 'success');
          // Log the activity
          await adminService.logAdminActivity('Logged out from all devices');
        } else {
          showSnackbar('Error logging out from all devices', 'error');
        }
      } catch (error) {
        showSnackbar('Error logging out from all devices', 'error');
      }
    }
  };

  return (
    <Box>
      <PageHeader
        title="Admin Profile"
        subtitle="Manage your account settings, security preferences, and profile information"
      />

      <Card sx={{ minHeight: '300px', overflow: 'visible' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="profile tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Profile Info" icon={<PersonIcon />} iconPosition="start" />
            <Tab label="Security" icon={<SecurityIcon />} iconPosition="start" />
            <Tab label="Preferences" icon={<NotificationsIcon />} iconPosition="start" />
            <Tab label="Activity Log" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Profile Info Tab */}
        <TabPanel value={currentTab} index={0}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    src={profileInfo.profilePicture}
                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                  >
                    {profileInfo.displayName.charAt(0)}
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    {profileInfo.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Administrator
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setEditingProfile(!editingProfile)}
                  >
                    {editingProfile ? 'Cancel' : 'Edit Profile'}
                  </Button>
                </Box>
              </Grid>

              <Grid xs={12} md={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Display Name"
                    value={profileInfo.displayName}
                    onChange={(e) => setProfileInfo({ ...profileInfo, displayName: e.target.value })}
                    disabled={!editingProfile}
                    fullWidth
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ color: 'action.active', mr: 1 }} />,
                    }}
                  />
                  <TextField
                    label="Email"
                    value={profileInfo.email}
                    onChange={(e) => setProfileInfo({ ...profileInfo, email: e.target.value })}
                    disabled={!editingProfile}
                    fullWidth
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ color: 'action.active', mr: 1 }} />,
                    }}
                  />
                  <TextField
                    label="Phone"
                    value={profileInfo.phone}
                    onChange={(e) => setProfileInfo({ ...profileInfo, phone: e.target.value })}
                    disabled={!editingProfile}
                    fullWidth
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ color: 'action.active', mr: 1 }} />,
                    }}
                  />

                  {editingProfile && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleProfileSave}
                        disabled={loading}
                      >
                        Save Changes
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={() => setEditingProfile(false)}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={currentTab} index={1}>
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                  Password & Authentication
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<LockIcon />}
                    onClick={() => setPasswordChangeOpen(true)}
                    fullWidth
                    sx={{ py: 1.5, fontWeight: 500 }}
                  >
                    Change Password
                  </Button>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={securitySettings.twoFactorEnabled}
                        onChange={(e) => setSecuritySettings({ 
                          ...securitySettings, 
                          twoFactorEnabled: e.target.checked 
                        })}
                      />
                    }
                    label="Enable Two-Factor Authentication"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: '0.95rem',
                        fontWeight: 500 
                      } 
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                  Session Management
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DevicesIcon />}
                    onClick={handleLogoutAllDevices}
                    color="warning"
                    fullWidth
                    sx={{ py: 1.5, fontWeight: 500 }}
                  >
                    Logout from All Devices
                  </Button>
                  <TextField
                    label="Session Timeout (minutes)"
                    type="number"
                    value={securitySettings.sessionTimeout}
                    onChange={(e) => setSecuritySettings({ 
                      ...securitySettings, 
                      sessionTimeout: Number(e.target.value) 
                    })}
                    fullWidth
                    variant="outlined"
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSecuritySave}
                    disabled={loading}
                    size="large"
                    sx={{ px: 4, py: 1.5, fontWeight: 600 }}
                  >
                    Save Security Settings
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Preferences Tab */}
        <TabPanel value={currentTab} index={2}>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4}>
              <Grid xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                  Notifications
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.emailNotifications}
                        onChange={(e) => setPreferences({ 
                          ...preferences, 
                          emailNotifications: e.target.checked 
                        })}
                      />
                    }
                    label="Email Notifications"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: '0.95rem',
                        fontWeight: 500 
                      } 
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fcmPermission === 'granted' || preferences.pushNotifications}
                        onChange={async (e) => {
                          if (e.target.checked) {
                            // Request browser permission when enabling
                            await requestNotificationPermission();
                          }
                          setPreferences({
                            ...preferences,
                            pushNotifications: e.target.checked
                          });
                        }}
                      />
                    }
                    label={
                      <Box>
                        Push Notifications
                        {fcmPermission === 'denied' && (
                          <Typography variant="caption" color="error" display="block">
                            Permission denied in browser. Please enable in browser settings.
                          </Typography>
                        )}
                        {fcmPermission === 'granted' && (
                          <Typography variant="caption" color="success.main" display="block">
                            Enabled
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.95rem',
                        fontWeight: 500
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.taskNotifications}
                        onChange={(e) => setPreferences({ 
                          ...preferences, 
                          taskNotifications: e.target.checked 
                        })}
                      />
                    }
                    label="Task Updates"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: '0.95rem',
                        fontWeight: 500 
                      } 
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.securityAlerts}
                        onChange={(e) => setPreferences({ 
                          ...preferences, 
                          securityAlerts: e.target.checked 
                        })}
                      />
                    }
                    label="Security Alerts"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: '0.95rem',
                        fontWeight: 500 
                      } 
                    }}
                  />
                </Box>
              </Grid>

              <Grid xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                  Display & Language
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.darkMode}
                        onChange={(e) => setPreferences({ 
                          ...preferences, 
                          darkMode: e.target.checked 
                        })}
                      />
                    }
                    label="Dark Mode"
                    sx={{ 
                      '& .MuiFormControlLabel-label': { 
                        fontSize: '0.95rem',
                        fontWeight: 500 
                      } 
                    }}
                  />
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      select
                      label="Language"
                      value={preferences.language}
                      onChange={(e) => setPreferences({ 
                        ...preferences, 
                        language: e.target.value 
                      })}
                      SelectProps={{ native: true }}
                    >
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                    </TextField>
                  </FormControl>
                  <TextField
                    label="Timezone"
                    value={preferences.timezone}
                    onChange={(e) => setPreferences({ 
                      ...preferences, 
                      timezone: e.target.value 
                    })}
                    fullWidth
                    variant="outlined"
                  />
                </Box>
              </Grid>

              <Grid xs={12}>
                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handlePreferenceSave}
                    disabled={loading}
                    size="large"
                    sx={{ px: 4, py: 1.5, fontWeight: 600 }}
                  >
                    Save Preferences
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Activity Log Tab */}
        <TabPanel value={currentTab} index={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Admin Activity
            </Typography>
            <List>
              {activityLog.map((activity) => (
                <ListItem key={activity.id} divider>
                  <ListItemIcon>
                    <HistoryIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.action}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="caption">
                          {activity.timestamp.toLocaleString()}
                        </Typography>
                        {activity.ip && activity.ip !== 'unknown' && (
                          <Chip label={activity.ip} size="small" variant="outlined" />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </TabPanel>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordChangeOpen} onClose={() => setPasswordChangeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Current Password"
              type={showPassword.current ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              fullWidth
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword({ ...showPassword, current: !showPassword.current })}
                    edge="end"
                  >
                    {showPassword.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              label="New Password"
              type={showPassword.new ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              fullWidth
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                    edge="end"
                  >
                    {showPassword.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              type={showPassword.confirm ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              fullWidth
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                    edge="end"
                  >
                    {showPassword.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordChangeOpen(false)}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained" disabled={loading}>
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminProfile;
